// Per-endpoint freshness window (fresh) and stale-acceptable window (stale).
// Anything older than `stale` is treated as cache-miss (refetch required).
// Anything within `fresh` doesn't trigger a refetch.
// Between `fresh` and `stale`: serve cached + refetch in background + show stale banner.

export type CachePolicy = { freshMs: number; staleMs: number };

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;

// Path → policy. Keys are exact paths (without query string).
// Use a function for parameterized routes like /me/trips/:id.
const POLICIES: Record<string, CachePolicy> = {
  // Today's schedule is operational state: allocations/unassignments must not
  // sit behind a minute-long freshness window. Push + foreground polling are
  // the primary refresh paths; this short window only coalesces rapid mounts.
  '/me/trips/today':   { freshMs: 10_000,    staleMs: 12 * HOUR },
  '/me/trips/history': { freshMs: 5 * MINUTE, staleMs: Number.MAX_SAFE_INTEGER },
  '/me/vehicle':       { freshMs: 5 * MINUTE, staleMs: 24 * HOUR },
  '/me':               { freshMs: 5 * MINUTE, staleMs: 24 * HOUR },
};

const TRIP_DETAIL_POLICY: CachePolicy = { freshMs: 10_000, staleMs: 4 * HOUR };

export function policyFor(path: string): CachePolicy | null {
  // Exact match first
  const exact = POLICIES[path];
  if (exact) return exact;
  // Trip detail: /me/trips/<uuid OR booking-code> (e.g. FL26-SC-7325). `today`
  // + `history` are exact-matched above, so they never reach this.
  if (/^\/me\/trips\/[A-Za-z0-9-]{6,}$/.test(path)) return TRIP_DETAIL_POLICY;
  return null;
}

/**
 * Reduce a full request URL OR a bare path to the canonical cache path used for
 * BOTH policy lookup and invalidation matching. Drops scheme/host + the `/v1`
 * prefix, the `/driver-app` segment (every cached driver endpoint lives under
 * it — the policy keys above are written without it), and the query string. So
 * `https://host/v1/driver-app/me/trips/FL26-SC-7325?x=1` and the bare path
 * `/driver-app/me/trips/FL26-SC-7325` both reduce to `/me/trips/FL26-SC-7325`.
 *
 * Historically this only stripped `/v1`, leaving `/driver-app/...` — which never
 * matched the `/me/...` policy keys, so the entire read-cache was a silent no-op.
 */
export function stripBase(urlOrPath: string): string {
  const v1 = urlOrPath.match(/\/v1(\/[^?]*)/);
  const path = v1 ? (v1[1] as string) : (urlOrPath.split('?')[0] ?? urlOrPath);
  return path.replace(/^\/driver-app(?=\/)/, '') || '/';
}

export function cacheState(fetchedAt: number, policy: CachePolicy): 'fresh' | 'stale' | 'expired' {
  const age = Date.now() - fetchedAt;
  if (age < policy.freshMs) return 'fresh';
  if (age < policy.staleMs) return 'stale';
  return 'expired';
}
